import React, { useEffect, useState } from "react";

import { navigate } from "@reach/router";
import { useWindowDimensions } from "react-native";
import { connect, ConnectedProps } from "react-redux";

// TOOD: replace imports w/ local scoped
import { CartReduxState } from "cuanto/features/Cart";
import * as catalogueActions from "cuanto/web-app/actions";
import NotFound from "cuanto/web-app/components/NotFound";
import { State as CatalogueState } from "cuanto/web-app/reducers";
// END TODO

import useTracker from "../../hooks/useTracker";
import LoadingView from "../components/LoadingView";
import useCatalogue from "../contexts/CatalogueContext";
import { isFromInstagramBrowser } from "../services/Tracking";
import { Product } from "../types";
import Catalogue from "./Catalogue";

type Props = ReduxProps & {
  username: string;
  tracker?: string;
  category_uuid?: string;
};

function CatalogueScreen(props: Props) {
  const {
    username,
    selectedCategoryUuid,
    cartQuantity,
    category_uuid,
    selectCategoryUuid,
    tracker,
  } = props;

  const { width } = useWindowDimensions();
  const numColumns = width < 600 || isFromInstagramBrowser() ? 2 : 3;

  const { catalogueData, priceGroups, isLoading, addToCart } = useCatalogue();

  // Initialize search from URL param
  const getSearchFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get("q") || "";
  };

  const [searchQuery, setSearchQuery] = useState(getSearchFromUrl);
  const [debouncedQuery, setDebouncedQuery] = useState(getSearchFromUrl);

  // Debounce search and update URL
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
      // Update URL without navigation
      const url = new URL(window.location.href);
      if (searchQuery) {
        url.searchParams.set("q", searchQuery);
      } else {
        url.searchParams.delete("q");
      }
      window.history.replaceState({}, "", url.toString());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  function handleClearSearch() {
    setSearchQuery("");
    setDebouncedQuery("");
    // Clear URL param immediately
    const url = new URL(window.location.href);
    url.searchParams.delete("q");
    window.history.replaceState({}, "", url.toString());
  }

  // Helper to build URL with search param preserved
  function buildUrl(path: string) {
    const searchParam = searchQuery
      ? `?q=${encodeURIComponent(searchQuery)}`
      : "";
    return `${path}${searchParam}`;
  }

  useTracker(tracker);

  if (isLoading) return <LoadingView />;

  if (!catalogueData) return <NotFound />;

  if (
    !catalogueData.merchant.has_ecommerce_store ||
    !catalogueData.products.length
  ) {
    navigate(`/${username}/c`);
  }

  const storeClosed = catalogueData.settings.some(
    (s) => s.key === "store_closed" && s.value === "t"
  );

  function onAddToCart(product: Product) {
    const { redirectTo } = addToCart(product);
    const shortUuid = (product.product_group_uuid || product.uuid).slice(-6);
    switch (redirectTo) {
      case "prices":
        navigate(`/${username}/p/${shortUuid}/prices`);
        break;
      case "cart":
        navigate(`/${username}/cart`);
        break;
      case "catalogue": // no-op, we're already here
      case null:
        break;
    }
  }

  function onGoToCart() {
    navigate(`/${username}/cart`);
  }

  function onSelectCategory(uuid: string) {
    selectCategoryUuid(uuid);

    if (uuid === "all") {
      navigate(buildUrl(`/${username}`));
    } else {
      navigate(buildUrl(`/${username}/category/${uuid}`));
    }
  }

  function onSelectProduct(product: Product) {
    navigate(`/${username}/p/${product.short_uuid}`);
  }

  const categoryUuid = category_uuid || selectedCategoryUuid;

  const filteredProducts = filterProducts(
    catalogueData.products,
    categoryUuid,
    debouncedQuery
  );

  const productGroups: Record<string, Product[]> = {};
  catalogueData.products.forEach((product) => {
    if (!product.product_group_uuid) return;
    if (!productGroups[product.product_group_uuid]) {
      productGroups[product.product_group_uuid] = [];
    }
    productGroups[product.product_group_uuid].push(product);
  });

  function navigateToMoreInfo() {
    navigate(`/${username}/more_info`);
  }

  return (
    <Catalogue
      merchant={catalogueData.merchant}
      products={filteredProducts}
      categories={catalogueData.categories}
      productGroups={productGroups}
      selectedCategoryUuid={categoryUuid}
      storeConfig={catalogueData.store_config}
      reviewsAverage={catalogueData.reviews_average}
      totalTransactionCount={catalogueData.total_transaction_count}
      deliveryEnabled={catalogueData.has_fulfillments_enabled}
      cartQuantity={cartQuantity}
      storeClosed={storeClosed}
      numCatalogueColumns={numColumns}
      priceGroups={priceGroups}
      onAddToCart={onAddToCart}
      onGoToCart={onGoToCart}
      onSelectProduct={onSelectProduct}
      onSelectCategory={onSelectCategory}
      onMoreInfoPress={navigateToMoreInfo}
      search={{
        query: searchQuery,
        debouncedQuery,
        onChange: setSearchQuery,
        onClear: handleClearSearch,
      }}
    />
  );
}

type State = { catalogue: CatalogueState; cart: CartReduxState };
const mapStateToProps = ({ catalogue, cart }: State) => ({
  selectedCategoryUuid: catalogue.selectedCategoryUuid,
  cartQuantity: cart.quantity,
});

const mapDispatchToProps = {
  selectCategoryUuid: catalogueActions.selectCategoryUuid,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(CatalogueScreen);

function filterProducts(
  products: Product[],
  selectCategoryUuid: string,
  searchQuery: string
) {
  return products.filter((p) => {
    if (!p.published) return false;
    if (p.product_group_uuid !== null) return false;

    if (selectCategoryUuid !== "all") {
      const uuids = p.categories.map((c) => c.uuid);
      if (!uuids.includes(selectCategoryUuid)) return false;
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase().trim();
      const name = p.name.toLowerCase().includes(q);
      const desc = p.description.toLowerCase().includes(q);
      const benefits = p.benefits.some((b) => b.toLowerCase().includes(q));

      return name || desc || benefits;
    }

    return true;
  });
}
